// src/components/Sidebar.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import RenameConversationModal from './RenameConversationModal';
import DeleteConversationModal from './DeleteConversationModal';
import AuthModal from './AuthModal';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ReactComponent as DashboardsIcon } from '../assets/sidebar/Dashboards.svg';
import { ReactComponent as PortfolioIcon } from '../assets/sidebar/Portfolio.svg';
import { ReactComponent as WorkflowsIcon } from '../assets/sidebar/Workflows.svg';
import { ReactComponent as HistoryIconSidebar } from '../assets/sidebar/History.svg';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
// import PushPinIcon from '@mui/icons-material/PushPin';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardItem from './DashboardItem';
import { styled, useTheme } from '@mui/material/styles';
import logoshort from '../assets/vester-logo-short.svg';
import logolong from '../assets/vester-logo-long.png';
import { ReactComponent as CustomChatIcon } from '../assets/write-icon.svg';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReactComponent as EditIcon } from '../assets/edit.svg';
import DiscordIcon from '../assets/discord.svg';
import rightUpArrow from '../assets/right-up-arrow.svg';
import XIcon from '@mui/icons-material/X';

// import chatIcon from '../assets/chat-icon.svg';

// Thunks and actions from conversationSlice
import {
  fetchConversations,
  setActiveConversation,
} from '../store/conversationSlice';

// Thunks and actions from dashboardSlice
import { fetchDashboards } from '../store/dashboardSlice';

const drawerWidthOpen = 225;
const drawerWidthClosed = 65;

const StyledDrawer = styled(Drawer)(({ theme, open, isPinned }) => ({
  width: open ? drawerWidthOpen : drawerWidthClosed,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
  position: isPinned ? 'static' : 'fixed',
  zIndex: isPinned ? 'auto' : 1200,
  '& .MuiDrawer-paper': {
    width: open ? drawerWidthOpen : drawerWidthClosed,
    backgroundColor: open ? 'rgb(240, 240, 240, 0.7)' : 'rgb(240, 240, 240, 0.5)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    color: theme.palette.sidebar.text,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    boxShadow: !isPinned && open ? '0px 2px 200px rgba(0, 0, 0, 0.25)' : 'none',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme, isDelete }) => ({
  display: 'flex',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '12px',
  margin: '2px 0',
  transition: '0.2s ease',
  background: 'transparent',
  '&:hover': {
    background: isDelete 
      ? 'rgba(211, 47, 47, 0.1)'
      : 'rgba(18, 157, 232, 0.1)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  '& .MuiSvgIcon-root': {
    color: isDelete ? '#d32f2f' : '#000000',
  },
  '& .MuiTypography-root': {
    color: isDelete ? '#d32f2f' : '#000000',
    fontWeight: '400',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    background: 'transparent',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1.2px solid rgba(200, 200, 200, 0.5)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
    padding: '0px 8px',
    width: '115px',
  },
  '& .MuiList-root': {
    padding: '4px 0px',
  },
}));

const ConversationRow = React.memo(function ConversationRow({
  conversationId,
  title,
  isActive,
  isVisuallyOpen,
  onSelect,
  onMenuOpen,
}) {
  const handleClick = useCallback(() => onSelect(conversationId), [onSelect, conversationId]);
  const handleMenuClick = useCallback(
    (e) => {
      e.stopPropagation();
      onMenuOpen(e, conversationId);
    },
    [onMenuOpen, conversationId]
  );

  return (
    <ListItem
      button
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: isActive ? 'rgba(18, 157, 232, 0.1)' : 'transparent',
        borderRadius: '8px',
        mx: 1,
        width: 'calc(100% - 16px)',
        padding: '2px 8px',
        '&:hover': {
          backgroundColor: isActive ? 'rgba(0, 123, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
          '& .more-options': {
            opacity: 1,
          },
        },
      }}
    >
      {isVisuallyOpen && (
        <>
          <ListItemText
            primary={title || 'Untitled Chat'}
            sx={{
              maxWidth: '180px',
              '& .MuiListItemText-primary': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '13px',
              },
            }}
          />
          <IconButton
            className="more-options"
            edge="end"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s',
              zIndex: 1,
              borderRadius: '4px',
              padding: '4px',
              ml: 'auto',
              mr: 0,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            onClick={handleMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
        </>
      )}
    </ListItem>
  );
});

const Sidebar = ({ isHovered, onHoverChange, isPinned = false, viewMode, onRequestStartView }) => {
  const navigationDisabled = viewMode === 'streaming';
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const activeConversationId = useSelector(
    (state) => state.conversation.activeConversation?.conversation_id
  );
  const [conversationToDelete, setConversationToDelete] = React.useState(null);
  const [conversationToRename, setConversationToRename] = React.useState(null);
  const [isLoadingDashboards, setIsLoadingDashboards] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isSignInMode, setIsSignInMode] = useState(true);
  const conversationListRef = useRef(null);
  const [isDashboardsDropdownOpen, setIsDashboardsDropdownOpen] = useState(true);
  const [isConversationsDropdownOpen, setIsConversationsDropdownOpen] = useState(true);
  const [internalHovered, setInternalHovered] = useState(false);


  // Narrow selectors to reduce Sidebar rerenders on unrelated slice updates
  const conversationList = useSelector((state) => state.conversation.conversationList);
  const pagination = useSelector((state) => state.conversation.pagination, shallowEqual);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userId = useSelector((state) => state.auth.user?.user?.id);
  const dashboardList = useSelector((state) => state.dashboard.dashboardList);

  const conversationsById = useMemo(() => {
    const map = new Map();
    for (const c of conversationList) {
      map.set(c.conversation_id, c);
    }
    return map;
  }, [conversationList]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      setIsLoadingConversations(true);
      dispatch(fetchConversations({ user_id: userId }))
        .finally(() => setIsLoadingConversations(false));
    }
  }, [isAuthenticated, userId, dispatch]);

  // Load more conversations when scrolling to bottom
  const handleScroll = useCallback(() => {
    if (!conversationListRef.current || isLoadingConversations) return;
    
    const { scrollTop, scrollHeight, clientHeight } = conversationListRef.current;
    
    // Check if scrolled to near bottom (within 20px)
    if (scrollHeight - scrollTop - clientHeight < 20) {
      // If there are more pages to load
      if (pagination.conversations.hasMore) {
        if (userId) {
          setIsLoadingConversations(true);
          dispatch(fetchConversations({
            user_id: userId,
            pageNum: pagination.conversations.currentPage + 1
          })).finally(() => setIsLoadingConversations(false));
        }
      }
    }
  }, [
    isLoadingConversations, 
    pagination.conversations.hasMore, 
    pagination.conversations.currentPage, 
    userId,
    dispatch
  ]);

  // Similar to conversations, fetch dashboards when authenticated
  useEffect(() => {
    if (isAuthenticated && userId) {
      setIsLoadingDashboards(true);
      dispatch(fetchDashboards(userId))
        .finally(() => setIsLoadingDashboards(false));
    }
  }, [isAuthenticated, userId, dispatch]);

  // Handler for creating a new chat
  const handleNewChat = () => {
    if (navigationDisabled) return; // Block navigation during streaming
    navigate('/');
  };

  // Handler for My Dashboards click
  const handleDashboardsClick = () => {
    if (navigationDisabled) return;
    // Existing behaviour (currently tooltip only)
    return;
  };

  // Auth modal handlers
  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleAuthModeToggle = (isSignIn) => {
    setIsSignInMode(isSignIn);
  };

  // Handler for when a conversation is clicked
  const handleConversationSelect = useCallback((conversationId) => {
    if (navigationDisabled) return; // Block navigation during streaming
    if (!conversationId) return;
    // Don't do anything if clicking on the already active conversation
    if (activeConversationId === conversationId) return;

    const conversation = conversationsById.get(conversationId);
    if (!conversation) return;

    // Request Start view mode when switching conversations (does not alter Conversation.js logic)
    try { if (typeof onRequestStartView === 'function') onRequestStartView(); } catch (e) {}

    dispatch(setActiveConversation({
      ...conversation,
      onChainMessages: null,
      newsMessages: null,
    }));
    navigate(`/conversation/${conversationId}`);
  }, [navigationDisabled, activeConversationId, conversationsById, dispatch, navigate, onRequestStartView]);

  const handleMenuOpen = useCallback((event, conversationId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversation(conversationsById.get(conversationId) || null);
  }, [conversationsById]);

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleRenameClick = () => {
    setConversationToRename(selectedConversation);
    setIsRenameModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    // Avoid noisy logs in production; leaving behavior unchanged.
    setConversationToDelete(selectedConversation);
    setIsDeleteModalOpen(true);
    handleMenuClose();
  };





  useEffect(() => {
    return () => {
      setMenuAnchorEl(null);
      setSelectedConversation(null);
      setConversationToDelete(null);
      setIsRenameModalOpen(false);
      setIsDeleteModalOpen(false);
      setConversationToRename(null);
    };
  }, []);

  // If pinned (home page), always open. Otherwise, open only on hover.
  // IMPORTANT: keep hover state local when parent doesn't provide it, so we don't rerender the entire app on hover.
  const effectiveHovered = typeof isHovered === 'boolean' ? isHovered : internalHovered;
  const setHovered = typeof onHoverChange === 'function' ? onHoverChange : setInternalHovered;
  const isVisuallyOpen = isPinned || effectiveHovered;

  return (
    <StyledDrawer
      variant="permanent"
      open={isVisuallyOpen}
      isPinned={isPinned}
      onMouseEnter={() => !isPinned && setHovered(true)}
      onMouseLeave={() => !isPinned && setHovered(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isVisuallyOpen ? 'left' : 'center',
          padding: '8px 8px 2px 8px',
          height: '40px',
        }}
      >
        {isVisuallyOpen ? (
          <img src={logolong} alt="Company Logo" style={{ height: '40px' }} />
        ) : (
          <img src={logoshort} alt="Company Logo" style={{ height: '32px' }} />
        )}
      </Box>

      {isVisuallyOpen ? (
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Webkit scrollbar styling (Chrome, Safari, newer Edge)
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#bfbfbf',
            borderRadius: '2px',
            '&:hover': {
              backgroundColor: '#a6a6a6',
            },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'none',
          '&': {
            scrollbarWidth: 'thin',
            scrollbarColor: '#bfbfbf transparent',
          },
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          {/* "New Analysis" Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '12px 0px 8px 0px', marginBottom: '4px' }}>
            <Button
              onClick={handleNewChat}
              sx={{
                width: '210px',
                height: '36px',
                background: '#ffffff',
                border: '1px solid rgba(180, 180, 180, 0.8)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                textTransform: 'none',
                color: '#000000',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: '#f5f5f5',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 20, ml: -1.5 }} />
              New Analysis
            </Button>
          </Box>

          {/* Navigation Menu Items */}
          <List sx={{ padding: '0px 8px', mb: 1 }}>
            {/* Dashboards */}
            <Tooltip
              title="Create a dashboard from your conversations to get started. Dashboards home page coming soon!"
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#000',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    fontSize: '14px',
                    border: '1px solid rgba(200, 200, 200, 0.6)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(255, 255, 255, 0.95)',
                    '&::before': {
                      border: '1px solid rgba(200, 200, 200, 0.6)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxSizing: 'border-box',
                    },
                  },
                },
              }}
            >
              <ListItem
                button
                onClick={handleDashboardsClick}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <DashboardsIcon style={{ width: 20, height: 20, marginRight: 12, fill: '#4A4A53' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1f1f1f' }}>
                  Dashboards
                </Typography>
              </ListItem>
            </Tooltip>

            {/* Portfolio */}
            <Tooltip
              title="Portfolio page coming soon."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#000',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    fontSize: '14px',
                    border: '1px solid rgba(200, 200, 200, 0.6)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(255, 255, 255, 0.95)',
                    '&::before': {
                      border: '1px solid rgba(200, 200, 200, 0.6)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxSizing: 'border-box',
                    },
                  },
                },
              }}
            >
              <ListItem
                button
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <PortfolioIcon style={{ width: 20, height: 20, marginRight: 12 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1f1f1f' }}>
                  Portfolio
                </Typography>
              </ListItem>
            </Tooltip>

            {/* Workflows */}
            <Tooltip
              title="Workflows page coming soon."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#000',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    fontSize: '14px',
                    border: '1px solid rgba(200, 200, 200, 0.6)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(255, 255, 255, 0.95)',
                    '&::before': {
                      border: '1px solid rgba(200, 200, 200, 0.6)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxSizing: 'border-box',
                    },
                  },
                },
              }}
            >
              <ListItem
                button
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <WorkflowsIcon style={{ width: 20, height: 20, marginRight: 12 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1f1f1f' }}>
                  Workflows
                </Typography>
              </ListItem>
            </Tooltip>

            {/* History */}
            <Tooltip
              title="Analysis history page coming soon."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#000',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    fontSize: '14px',
                    border: '1px solid rgba(200, 200, 200, 0.6)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(255, 255, 255, 0.95)',
                    '&::before': {
                      border: '1px solid rgba(200, 200, 200, 0.6)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxSizing: 'border-box',
                    },
                  },
                },
              }}
            >
              <ListItem
                button
                onClick={handleNewChat}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <HistoryIconSidebar style={{ width: 20, height: 20, marginRight: 12 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1f1f1f' }}>
                  History
                </Typography>
              </ListItem>
            </Tooltip>
          </List>

          {/* Divider */}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 1 }}>
            <Divider sx={{ width: '90%', borderWidth: 1, borderColor: theme.palette.divider }} />
          </Box>

          {/* Dashboards Dropdown */}
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: 2,
                paddingRight: 1,
                paddingY: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
              }}
              onClick={() => setIsDashboardsDropdownOpen(!isDashboardsDropdownOpen)}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#444746',
                  fontSize: '13px',
                }}
              >
                Dashboards
              </Typography>
              <IconButton size="small" sx={{ padding: '2px' }}>
                {isDashboardsDropdownOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>

            {isDashboardsDropdownOpen && (
              <List sx={{
                padding: '0px 0px 0px 0px',
                maxHeight: '148px', // 4 items max
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0,0,0,0.12) transparent',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.12)',
                  borderRadius: '4px',
                },
              }}>
                {isLoadingDashboards ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} sx={{ color: theme.palette.sidebar.text }} />
                  </Box>
                ) : dashboardList.length === 0 ? (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                      No dashboards yet
                    </Typography>
                  </Box>
                ) : (
                  dashboardList.map((dashboard) => {
                    const isActive = location.pathname.startsWith(`/dashboard/${dashboard.dashboard_id}`);
                    return (
                      <DashboardItem
                        key={dashboard.dashboard_id}
                        dashboard={dashboard}
                        isOpen={isVisuallyOpen}
                        isActive={isActive}
                        navigationDisabled={navigationDisabled}
                      />
                    );
                  })
                )}
              </List>
            )}
          </Box>

          {/* Recent Conversations Dropdown */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: 2,
                paddingRight: 1,
                paddingY: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
              }}
              onClick={() => setIsConversationsDropdownOpen(!isConversationsDropdownOpen)}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#444746',
                  fontSize: '13px',
                }}
              >
                Conversations
              </Typography>
              <IconButton size="small" sx={{ padding: '2px' }}>
                {isConversationsDropdownOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>

            {isConversationsDropdownOpen && (
              <List
                sx={{
                  pt: '4px',
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0,0,0,0.12) transparent',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.12)',
                    borderRadius: '4px',
                  },
                }}
                ref={conversationListRef}
                onScroll={handleScroll}
              >
                {isLoadingConversations && conversationList.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} sx={{ color: theme.palette.sidebar.text }} />
                  </Box>
                ) : conversationList.length === 0 ? (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                      No conversations yet
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {conversationList.map((conv) => (
                      <ConversationRow
                        key={conv.conversation_id}
                        conversationId={conv.conversation_id}
                        title={conv.title}
                        isActive={activeConversationId === conv.conversation_id}
                        isVisuallyOpen={isVisuallyOpen}
                        onSelect={handleConversationSelect}
                        onMenuOpen={handleMenuOpen}
                      />
                    ))}

                    {/* Loading indicator at the end when fetching more */}
                    {isLoadingConversations && conversationList.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                        <CircularProgress size={16} sx={{ color: theme.palette.sidebar.text }} />
                      </Box>
                    )}
                  </>
                )}
              </List>
            )}
          </Box>


          {/* Community Discord Banner */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, background: 'radial-gradient(ellipse 236.96% 236.96% at 50.26% -155.98%, rgba(18, 157, 232, 0.50) 0%, rgba(136.50, 206, 243.50, 0.75) 41%, white 100%)', borderRadius: '10px', width: '190px', height: '115px', margin: 'auto auto 6px auto', flexDirection: 'column' }}>
              <Typography sx={{color: 'black', fontSize: 16, fontWeight: '500', wordWrap: 'break-word', marginLeft: '6px'}}>Join the Community</Typography>
              <Typography sx={{maxWidth: '90%', color: '#71717A', fontSize: 11, fontWeight: '500', wordWrap: 'break-word', whiteSpace: "normal", marginLeft: '6px'}}>Report bugs, share ideas, and get the latest announcements from the Vester team</Typography>
              
              <Button sx={{width: '100%', height: 28, background: '#F4F4F4', borderRadius: 2, marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', textTransform: 'none',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.015)',
                },
              }} onClick={() => window.open('https://discord.gg/FRd2TRH7qv', '_blank')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={DiscordIcon}
                    alt="Discord Icon"
                    style={{ width: 15, height: 15, marginRight: '5px' }}
                  />
                  <Typography
                    sx={{
                      color: '#18181B',
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    Open Discord
                  </Typography>
                </Box>
                <img src={rightUpArrow} alt="Right Up Arrow Icon" style={{width: 13, height: 13, marginLeft: 'auto', marginRight: '2px'}} />
              </Button>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            gap: 1,
            px: 0, 
            pb: 1,
            width: '200px',
            margin: '0 auto'
          }}>
            <Button
              sx={{
                flex: 1,
                height: 32,
                background: `radial-gradient(
                  ellipse 250% 250% at 50% -50%,
                  rgba(18,157,232,0.5) 0%,
                  rgba(136,206,243,0.4) 41%,
                  #ffffff 100%
                )`,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '11px',
                fontWeight: 500,
                color: 'black',
                '&:hover': {
                  transform: 'scale(1.02)',
                  background: `radial-gradient(
                    ellipse 250% 250% at 50% -50%,
                    rgba(18,157,232,0.6) 0%,
                    rgba(136,206,243,0.85) 41%,
                    #ffffff 100%
                  )`,
                },
                transition: 'transform 0.2s ease',
              }}
              onClick={() => window.open('https://the-vester-pulse.beehiiv.com/subscribe', '_blank')}
            >
              <MailRoundedIcon sx={{ fontSize: 16, color: 'black', mr: '6px' }} />
              Get the Vester Pulse
            </Button>
            
            <IconButton
              sx={{
                width: 32,
                height: 32,
                background: '#ffffff',
                borderRadius: 2,
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                transition: 'transform 0.2s ease',
              }}
              onClick={() => window.open('https://x.com/VesterAI', '_blank')}
            >
              <XIcon sx={{ fontSize: 16, color: 'black' }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '6px' }}>
          <Tooltip title="Create New Chat" placement="right">
            <IconButton onClick={handleNewChat} sx={{ color: theme.palette.sidebar.text, mb: '5px' }}>
              <CustomChatIcon width={24} height={24} />
            </IconButton>
          </Tooltip>
          <Divider sx={{ width: '50%', borderWidth: 1, borderColor: theme.palette.divider, mb: '5px' }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '10px' }}>
          <DashboardsIcon style={{ width: 20, height: 20, fill: '#4A4A53' }} />
          <PortfolioIcon style={{ width: 22, height: 22, marginLeft: '-1px' }} />
          <WorkflowsIcon style={{ width: 20, height: 20 }} />
          <HistoryIconSidebar style={{ width: 22, height: 22, marginLeft: '-1px' }} />
          </Box>

          {/* Social icons at bottom of closed sidebar */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1,
            mt: 'auto',
            mb: 2,
          }}>
            <IconButton
              sx={{
                width: 32,
                height: 32,
                background: '#ffffff',
                borderRadius: 2,
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                transition: 'transform 0.2s ease',
              }}
              onClick={() => window.open('https://discord.com/invite/X9CKyVz5', '_blank')}
            >
              <Box
                component="img"
                src={DiscordIcon}
                alt="Discord Icon"
                sx={{ width: 24, height: 24 }}
              />
            </IconButton>
            
            <IconButton
              sx={{
                width: 32,
                height: 32,
                background: '#ffffff',
                borderRadius: 2,
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                transition: 'transform 0.2s ease',
              }}
              onClick={() => window.open('https://x.com/VesterAI', '_blank')}
            >
              <XIcon sx={{ fontSize: 16, color: 'black' }} />
            </IconButton>
          </Box>
        </Box>
      )}


      {/* Context Menu */}
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ ml: '17px', mt: '-8px' }}
      >
        <StyledMenuItem onClick={handleRenameClick}>
          <EditIcon style={{ width: 16, height: 16 }} />
          <Typography sx={{ml: '1.5px', fontSize: '14px'}}>Rename</Typography>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleDeleteClick} isDelete>
          <DeleteIcon style={{ width: 18, height: 18 }}/>
          <Typography sx={{fontSize: '14px'}}>Delete</Typography>
        </StyledMenuItem>
      </StyledMenu>

      {/* Rename Modal */}
      <RenameConversationModal
        open={isRenameModalOpen}
        onClose={() => {
          setIsRenameModalOpen(false);
          setConversationToRename(null);
        }}
        conversation={conversationToRename}
      />

      {/* Delete Modal */}
      <DeleteConversationModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setConversationToDelete(null);
        }}
        conversation={conversationToDelete}
        onDeleteStart={() => setIsLoadingConversations(true)}
      />

      {/* Auth Modal */}
      <AuthModal 
        open={authModalOpen} 
        onClose={handleAuthModalClose} 
        showSignIn={isSignInMode}
        onToggleMode={handleAuthModeToggle}
      />
    </StyledDrawer>
  );
};

export default React.memo(Sidebar);

